import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Customer360ActivityCategory, Customer360ActivityPage, Customer360ActivityType, Customer360Overview, Customer360QuickActionId } from "@cream/shared";
import { http } from "../../services/apiClient";

export const QO = { retry: 1, refetchOnWindowFocus: false } as const;

export const queryKeys = {
  orders: (p?: any) => ["orders", p] as const,
  order: (id: string) => ["order", id] as const,
  customerSearch: (q: string) => ["cs", q] as const,
  customer: (id: string) => ["cust", id] as const,
  customer360: (id: string) => ["customer-360", id] as const,
  customer360Activity: (id: string, p?: any) => ["customer-360-activity", id, p] as const,
  notifs: () => ["notifs"] as const,
  rules: () => ["rules"] as const,
  calendar: (from: string, to: string) => ["cal", from, to] as const,
  memory: (cid: string) => ["mem", cid] as const,
  followUps: () => ["fu"] as const,
  revenue: () => ["rev"] as const,
  risks: () => ["risks"] as const,
  staff: () => ["staff"] as const,
  feedback: () => ["fbk"] as const,
  assist: (role: string) => ["assist", role] as const,
  health: () => ["health"] as const,
  users: () => ["users"] as const,
  approvals: () => ["approvals"] as const,
  audit: (p: number) => ["audit", p] as const,
  delivery: () => ["delivery"] as const,
  invoices: () => ["invoices"] as const,
  marketing: () => ["mkt"] as const,
  charge: (addr: string) => ["charge", addr] as const,
  search: (q: string) => ["search", q] as const,
  inquiries: (p?: any) => ["inq", p] as const,
  gst: (m: string, y: string) => ["gst", m, y] as const,
  followUpTasks: (p?: any) => ["follow-up-tasks", p] as const,
  whatsAppConversations: (p?: any) => ["wa-conversations", p] as const,
  whatsAppConversation: (id: string) => ["wa-conversation", id] as const,
  whatsAppAssignableUsers: () => ["wa-assignable-users"] as const,
};
export const useOrders    = (p?: any) => useQuery({ queryKey: queryKeys.orders(p), queryFn: () => http.get("/orders", { params: p }).then(r => r.data), staleTime: 15000, refetchInterval: 30000, ...QO });
export const useOrder     = (id: string) => useQuery({ queryKey: queryKeys.order(id), queryFn: () => http.get(`/orders/${id}`).then(r => r.data.data), enabled: !!id, ...QO });
export const useCustSrch  = (q: string) => {
  const normalizedQuery = q.trim();

  return useQuery({
    queryKey: queryKeys.customerSearch(normalizedQuery),
    queryFn: () =>
      http.get("/customers", {
        params: { q: normalizedQuery },
      }).then(r => r.data.data),
    enabled: normalizedQuery.length >= 2,
    staleTime: 30000,
    ...QO,
  });
};
export const useCust      = (id: string) => useQuery({ queryKey: queryKeys.customer(id), queryFn: () => http.get(`/customers/${id}`).then(r => r.data.data), enabled: !!id, ...QO });
export const useCustomer360 = (id: string) => useQuery<Customer360Overview>({ queryKey: queryKeys.customer360(id), queryFn: () => http.get(`/customers/${id}/360`).then(r => r.data.data as Customer360Overview), enabled: !!id, staleTime: 30000, ...QO });
export const useCustomer360Activity = (
  id: string,
  filters: { category?: Customer360ActivityCategory; type?: Customer360ActivityType } = {},
  enabled = true,
) => useInfiniteQuery<Customer360ActivityPage>({
  queryKey: queryKeys.customer360Activity(id, filters),
  queryFn: ({ pageParam }) => http.get(`/customers/${id}/activity`, { params: cleanPayload({ ...filters, cursor: pageParam as string | undefined, limit: 25 }) }).then(r => r.data.data as Customer360ActivityPage),
  enabled: !!id && enabled,
  initialPageParam: undefined,
  getNextPageParam: (lastPage) => lastPage.pageInfo.nextCursor ?? undefined,
  staleTime: 15000,
  ...QO,
});
export const useCustomer360WriteMutation = (customerId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { action: Customer360QuickActionId; payload: Record<string, any> }) => {
      const body = cleanPayload(input.payload);
      if (input.action === "EDIT_CUSTOMER") return http.patch(`/customers/${customerId}`, body).then(r => r.data.data);
      if (input.action === "ADD_ADDRESS") return http.post(`/customers/${customerId}/addresses`, body).then(r => r.data.data);
      if (input.action === "RECORD_OCCASION") return http.post(`/customers/${customerId}/occasions`, body).then(r => r.data.data);
      if (input.action === "ADD_PREFERENCE") return http.post(`/customers/${customerId}/preferences`, body).then(r => r.data.data);
      if (input.action === "ADD_NOTE") return http.post(`/customers/${customerId}/notes`, body).then(r => r.data.data);
      if (input.action === "ASSIGN_RM") return http.post(`/customers/${customerId}/relationship-assignment`, body).then(r => r.data.data);
      throw new Error("Unsupported Customer 360 write action");
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.customer360(customerId) }),
        qc.invalidateQueries({ queryKey: ["customer-360-activity", customerId] }),
        qc.invalidateQueries({ queryKey: queryKeys.customer(customerId) }),
        qc.invalidateQueries({ queryKey: ["cs"] }),
      ]);
    },
  });
};
export const useNotifs    = () => useQuery({ queryKey: queryKeys.notifs(), queryFn: () => http.get("/notifications", { params: { limit: 30 } }).then(r => r.data), refetchInterval: 30000, staleTime: 0, ...QO });
export const useRules     = () => useQuery({ queryKey: queryKeys.rules(), queryFn: () => http.get("/admin/config").then(r => r.data.data), staleTime: 60000, ...QO });
export const useCal       = (from: string, to: string) => useQuery({ queryKey: queryKeys.calendar(from, to), queryFn: () => http.get("/calendar", { params: { from, to } }).then(r => r.data.data), enabled: !!from && !!to, ...QO });
export const useMemory    = (cid: string) => useQuery({ queryKey: queryKeys.memory(cid), queryFn: () => http.get(`/intelligence/memory/${cid}`).then(r => r.data.data), enabled: !!cid, staleTime: 120000, ...QO });
export const useFollowUps = (enabled = true) => useQuery({ queryKey: queryKeys.followUps(), queryFn: () => http.get("/intelligence/follow-ups").then(r => r.data.data), enabled, staleTime: 60000, ...QO });
export const useRevenue   = (enabled = true) => useQuery({ queryKey: queryKeys.revenue(), queryFn: () => http.get("/intelligence/revenue-forecast").then(r => r.data.data), enabled, staleTime: 60000, ...QO });
export const useRisks     = (enabled = true) => useQuery({ queryKey: queryKeys.risks(), queryFn: () => http.get("/intelligence/operational-risks").then(r => r.data.data), enabled, staleTime: 30000, ...QO });
export const useStaff     = (enabled = true) => useQuery({ queryKey: queryKeys.staff(), queryFn: () => http.get("/intelligence/staff-performance").then(r => r.data.data), enabled, staleTime: 120000, ...QO });
export const useFeedback  = (enabled = true) => useQuery({ queryKey: queryKeys.feedback(), queryFn: () => http.get("/intelligence/feedback-analysis").then(r => r.data.data), enabled, staleTime: 120000, ...QO });
export const useAssist    = (role: string) => useQuery({ queryKey: queryKeys.assist(role), queryFn: () => http.get(`/intelligence/assistant/${role}`).then(r => r.data.data), enabled: !!role, staleTime: 30000, refetchInterval: 60000, ...QO });
export const useHealth    = () => useQuery({ queryKey: queryKeys.health(), queryFn: () => http.get("/admin/system-health").then(r => r.data.data), refetchInterval: 30000, ...QO });
export const useUsers     = () => useQuery({ queryKey: queryKeys.users(), queryFn: () => http.get("/admin/users").then(r => r.data.data), ...QO });
export const useApprovals = () => useQuery({ queryKey: queryKeys.approvals(), queryFn: () => http.get("/admin/discount-approvals").then(r => r.data.data), refetchInterval: 30000, ...QO });
export const useAudit     = (p: number) => useQuery({ queryKey: queryKeys.audit(p), queryFn: () => http.get("/admin/audit-log", { params: { limit: 30, page: p } }).then(r => r.data), staleTime: 30000, ...QO });
export const useDelivery  = () => useQuery({ queryKey: queryKeys.delivery(), queryFn: () => http.get("/delivery/assignments").then(r => r.data.data), refetchInterval: 30000, ...QO });
export const useInvoices  = () => useQuery({ queryKey: queryKeys.invoices(), queryFn: () => http.get("/invoices", { params: { limit: 50 } }).then(r => r.data), ...QO });
export const useMktRpt    = () => useQuery({ queryKey: queryKeys.marketing(), queryFn: () => http.get("/reports/marketing").then(r => r.data.data), staleTime: 120000, ...QO });
export const useCharge    = (addr: string) => useQuery({ queryKey: queryKeys.charge(addr), queryFn: () => http.get("/delivery/calculate-charge", { params: { address: addr } }).then(r => r.data.data), enabled: addr.length > 10, staleTime: 300000, ...QO });
export const useSearch    = (q: string) => useQuery({ queryKey: queryKeys.search(q), queryFn: () => http.get("/search", { params: { q } }).then(r => r.data.data), enabled: q.length > 1, staleTime: 10000, ...QO });
export const useInquiries = (p?: any, enabled = true) => useQuery({ queryKey: queryKeys.inquiries(p), queryFn: () => http.get("/inquiries", { params: p }).then(r => r.data.data), enabled, ...QO });
export const useGST       = (m: string, y: string, enabled = true) => useQuery({ queryKey: queryKeys.gst(m, y), queryFn: () => http.get("/reports/gst", { params: { month: m, year: y } }).then(r => r.data.data), enabled, ...QO });
export const useFollowUpTasks = (p?: any, enabled = true) => useQuery({ queryKey: queryKeys.followUpTasks(p), queryFn: () => http.get("/follow-up-tasks", { params: p }).then(r => r.data.data), enabled, staleTime: 15000, refetchInterval: 30000, ...QO });
export const useWhatsAppConversations = (p?: any, enabled = true) => useQuery({
  queryKey: queryKeys.whatsAppConversations(p),
  queryFn: () =>
    http.get("/whatsapp/conversations", { params: p })
      .then(r => r.data.data),
  enabled,
  ...QO,
  staleTime: 0,
  refetchInterval: 2000,
  refetchIntervalInBackground: true,
  refetchOnWindowFocus: true,
});
export const useWhatsAppConversation = (id: string) => useQuery({
  queryKey: queryKeys.whatsAppConversation(id),
  queryFn: () =>
    http.get(`/whatsapp/conversations/${id}`)
      .then(r => r.data.data),
  enabled: !!id,
  ...QO,
  staleTime: 0,
  refetchInterval: id ? 2000 : false,
  refetchIntervalInBackground: true,
  refetchOnWindowFocus: true,
});
export const useWhatsAppAssignableUsers = (enabled: boolean) => useQuery({ queryKey: queryKeys.whatsAppAssignableUsers(), queryFn: () => http.get("/whatsapp/assignable-users").then(r => r.data.data), enabled, staleTime: 60000, ...QO });

function cleanPayload(input: Record<string, any>) {
  return Object.fromEntries(Object.entries(input).filter(([, value]) => value !== "" && value !== undefined));
}
